import { useCallback, useMemo, useRef } from 'react'
import { AnnouncementState, useMarkAnnouncementMutation, useMyEngagementQuery } from '@paintscout/api'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { isE2E } from 'shared/util/e2e'
import { track } from 'shared/util/analytics'
import type { AnalyticsEvent } from '@paintscout/util/analytics'
import { hasTriggerFired, triggerProperty } from '@paintscout/util/engagement'
import type { Announcement, AnnouncementStateName } from './announcementQueue'
import { hasState, sortNewestFirst, unreadCount, withState } from './announcementQueue'

/** Where the user met the announcement: it opened by itself, or they opened it from the header. */
export type AnnouncementSource = 'auto' | 'history'

const EVENTS: Record<AnnouncementStateName, AnalyticsEvent> = {
  viewed: 'announcement_viewed',
  dismissed: 'announcement_dismissed',
  ctaClicked: 'announcement_cta_clicked'
}

const STATES: Record<AnnouncementStateName, AnnouncementState> = {
  viewed: AnnouncementState.Viewed,
  dismissed: AnnouncementState.Dismissed,
  ctaClicked: AnnouncementState.CtaClicked
}

/**
 * The user's live announcements from `myEngagement` (#25343), newest first, and `mark` to record
 * their state. Off (empty, no request) when `announcementCenter` is off or during BugBug runs.
 *
 * `cache-first`: AuthorizeUser fires `myEngagement` at startup (#25347), so this normally reads the
 * cache. Without that query it fetches once itself.
 */
export function useAnnouncements() {
  const { announcementCenter } = useFlags()
  const enabled = !!announcementCenter && !isE2E()

  const { data } = useMyEngagementQuery({ fetchPolicy: 'cache-first', skip: !enabled })
  const [markAnnouncement] = useMarkAnnouncementMutation()

  const announcements = useMemo(
    () => (enabled ? sortNewestFirst(data?.myEngagement?.announcements ?? []) : []),
    [enabled, data]
  )
  // The history list and badge: a page-view announcement joins them only once it has shown (#25610).
  const history = useMemo(() => announcements.filter(hasTriggerFired), [announcements])
  // The latest cached copy, so an optimistic write never rolls back a timestamp set since the
  // caller took its copy (AnnouncementCenter pins the one it opened).
  const latestRef = useRef(announcements)
  latestRef.current = announcements

  const mark = useCallback(
    (given: Announcement, state: AnnouncementStateName, source: AnnouncementSource) => {
      const announcement = latestRef.current.find((a) => a.id === given.id) ?? given
      // Each timestamp is set once on the server, so a repeat is a no-op there; skip it here too,
      // so the event counts first occurrences only, as the admin counters do.
      if (!enabled || hasState(announcement, state)) return
      track(EVENTS[state], {
        announcementId: announcement.id,
        displayMode: announcement.displayMode,
        source,
        trigger: triggerProperty(announcement)
      })
      markAnnouncement({
        variables: { id: announcement.id, state: STATES[state] },
        optimisticResponse: {
          __typename: 'Mutation',
          markAnnouncement: {
            __typename: 'MyAnnouncement',
            ...withState(announcement, state, new Date().toISOString())
          }
        }
      }).catch(() => {
        // The announcement expired or was archived since it loaded. Nothing to show the user;
        // the next myEngagement fetch drops it.
      })
    },
    [enabled, markAnnouncement]
  )

  // `announcements` is every delivered one, for AnnouncementCenter to pick from. `history` and
  // `unread` leave out page-view ones that have not fired yet.
  return { enabled, announcements, history, unread: unreadCount(history), mark }
}
